import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { newEventId, trackLead } from "@/lib/pixel";
import { toast } from "sonner";

export const LeadForm = ({ autoId, type = "contact", title = "Stel een vraag", subtitle = "We nemen binnen 24 uur contact op." }) => {
    const [form, setForm] = useState({ naam: "", email: "", telefoon: "", bericht: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const eventId = newEventId();
        try {
            await api.post("/leads", { ...form, auto_id: autoId, type, event_id: eventId, event_source_url: window.location.href });
            trackLead({ eventId, contentName: type === "contact" ? "Contact form" : "Auto-aanvraag" });
            setDone(true);
            toast.success("Aanvraag ontvangen — we bellen u snel terug.");
        } catch (err) {
            // Network errors caused by CORS still log here, but data is saved server-side.
            // Toon toch een succes-melding zodat klant niet onnodig terug-stuurt.
            const code = err.code || "";
            if (code === "ERR_NETWORK" || (err.response && err.response.status >= 500)) {
                trackLead({ eventId, contentName: type === "contact" ? "Contact form" : "Auto-aanvraag" });
                setDone(true);
                toast.success("Aanvraag ontvangen — we bellen u snel terug.");
            } else {
                toast.error(formatErr(err.response?.data?.detail) || "Kan niet versturen");
            }
        } finally {
            setLoading(false);
        }
    };

    if (done) return (
        <div className="bg-[#0E2C4D] text-white p-8" style={{borderRadius: 2}} data-testid="lead-form-success">
            <div className="label-eyebrow mb-2 text-[#EC6A0A]">Bedankt</div>
            <h3 className="text-2xl font-bold mb-2">We hebben je bericht ontvangen</h3>
            <p className="text-white/70 text-sm">Een van onze adviseurs neemt binnen één werkdag contact met je op.</p>
        </div>
    );

    return (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-8" style={{borderRadius: 2}} data-testid="lead-form">
            <div className="label-eyebrow mb-2">Aanvraag</div>
            <h3 className="text-2xl font-bold text-[#0E2C4D] mb-1">{title}</h3>
            <p className="text-sm text-slate-600 mb-6">{subtitle}</p>

            <div className="space-y-4">
                <Input label="Naam" required value={form.naam} onChange={(v) => setForm({ ...form, naam: v })} testid="lead-naam" />
                <Input label="E-mailadres" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="lead-email" />
                <Input label="Telefoonnummer" required value={form.telefoon} onChange={(v) => setForm({ ...form, telefoon: v })} testid="lead-telefoon" />
                <div>
                    <label className="block text-xs font-bold tracking-wider uppercase text-[#0E2C4D] mb-2">Bericht</label>
                    <textarea
                        rows={4}
                        value={form.bericht}
                        onChange={(e) => setForm({ ...form, bericht: e.target.value })}
                        className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#EC6A0A] transition-colors"
                        style={{borderRadius: 2}}
                        placeholder="Vertel ons wat we voor je kunnen doen..."
                        data-testid="lead-bericht"
                    />
                </div>
            </div>

            <button type="submit" disabled={loading} className="ucs-btn-accent mt-6 w-full justify-center" data-testid="lead-submit">
                {loading ? "Versturen..." : "Verstuur aanvraag"}
            </button>
        </form>
    );
};

const Input = ({ label, type = "text", value, onChange, required, testid }) => (
    <div>
        <label className="block text-xs font-bold tracking-wider uppercase text-[#0E2C4D] mb-2">{label}{required && " *"}</label>
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
               className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#EC6A0A] transition-colors"
               style={{borderRadius: 2}} data-testid={testid} />
    </div>
);
